import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock'
import { useRouter } from 'next/router'
import { rem } from 'polished'
import React, { useEffect, useRef, useState } from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Box, Flex, Grid } from 'components/Layout'
import Link from 'components/Link'
import Logo from 'components/Logo'
import { MediaQueryWrapper } from 'components/MediaQueryWrapper'
import { bordered } from 'components/mixins'
import { Text } from 'components/Typography'
import { LIST_OF_PAGES_WITH_FULL_VIEWPORT, ROUTE } from 'constants/routes'
import { useIsVisible } from 'hooks'
import { BOX_SHADOW, COLOR, FONT_SIZE, RADIUS, SPACE, Z_INDEX } from 'Theme'
import { useMedia } from 'useMedia'
import { isIOS } from 'utils/userAgent'
import { BackButton } from './BackButton'
import { Content } from './Content'
import { Header } from './Header'
import { NavigationWrapper } from './NavigationWrapper'
import { NavLink } from './NavLink'
import { NavLinkWrapper } from './NavLinkWrapper'
import { Title } from './Title'

const AVATAR_SIZE = rem(24)
const POPUP_WIDTH = rem(250)
const LOGO_MENU_GAP = SPACE.PX_60
export const SIDEBAR_WIDTH = rem(200)
export const MOBILE_HEADER_HEIGHT = rem(70)
export const LOGOUT_WRAPPER_HEIGHT = rem(64)

const Avatar = styled(Flex)`
  border-radius: ${RADIUS.CIRCLE};
  background: ${({ imageUrl }) => `url("${imageUrl}") `};
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
`

const StyledGrid = styled(Grid)`
  background: ${COLOR.GRAYSCALE_6};
`

const Sidebar = styled(StyledGrid)`
  overflow-x: hidden;
  border-right: 1px solid ${COLOR.GRAYSCALE_5};
  /* https://github.com/sudolabs-io/the-expert/issues/2255 */
  ${({ isOnIOS }) => !isOnIOS && `max-height: none;`};
`

const Wrapper = styled(Flex)`
  background: ${COLOR.GRAYSCALE_6};
  transition: 300ms;
  ${({ isVisible }) =>
    isVisible
      ? `
          visibility: visible;
          opacity: 1;
        `
      : `
          visibility: hidden;
          opacity: 0;
        `};
`

const LogoutWrapper = styled(Wrapper)`
  cursor: pointer;
`

const LogoutPopup = styled(Flex)`
  box-shadow: ${BOX_SHADOW.ELEVATION_2};
  background: ${COLOR.WHITE};
  border-radius: ${RADIUS.PX_6};
  ${bordered};
`

const MobileHeader = styled(`header`)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const MenuIconWrapper = styled('button')`
  margin-right: ${SPACE.PX_20};
  user-select: none;
  &:hover {
    cursor: pointer;
  }
`

const ContentWrapper = styled(Box)`
  ${({ isFullViewport, isMobile }) =>
    isFullViewport &&
    `
    display: flex;
    flex-direction: column;
    ${isMobile ? '' : 'max-height: 100vh; height: 100vh;'}
  `}
`

interface SideNavPageProps {
  navigation?: (ref: React.MutableRefObject<any>) => React.ReactNode
  popupActionButtons?: () => React.ReactNode
  footerText: string
  footerImageUrl?: string
}

interface SideNavPageExtensions {
  BackButton: typeof BackButton
  Content: typeof Content
  Header: typeof Header
  NavigationWrapper: typeof NavigationWrapper
  NavLink: typeof NavLink
  NavLinkWrapper: typeof NavLinkWrapper
  Title: typeof Title
}

export const SideNavPage: React.FC<React.PWC<SideNavPageProps>> &
  SideNavPageExtensions = ({
  children,
  navigation,
  popupActionButtons,
  footerText,
  footerImageUrl = '/avatarPlaceholder.png',
}) => {
  const router = useRouter()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const media = useMedia()
  const isMobile = media.MOBILE
  const menuRef = useRef(null)

  useEffect(() => {
    const handleRouteChange = () => {
      if (isMobile) {
        setIsMenuOpen(false)
        clearAllBodyScrollLocks()
      }
    }
    router?.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router?.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [isMobile, router])

  const {
    ref: logoutRef,
    isVisible: isLogoutVisible,
    setIsVisible: setIsLogoutVisible,
  } = useIsVisible({ initialIsVisible: false })

  const isSidebarVisible =
    ((isMobile && isMenuOpen) || !isMobile) && media.isReady

  return (
    <StyledGrid
      height="100vh"
      minHeight="-webkit-fill-available"
      maxHeight="-webkit-fill-available"
      gridTemplateColumns={{ TABLET: `${SIDEBAR_WIDTH} 1fr` }}
      gridTemplateRows={{
        MOBILE: `${MOBILE_HEADER_HEIGHT} auto`,
        TABLET: 'unset',
      }}
    >
      <Sidebar
        height="100vh"
        minHeight="-webkit-fill-available"
        maxHeight="-webkit-fill-available"
        isOnIOS={isIOS()}
        gridTemplateRows={{
          MOBILE: `${MOBILE_HEADER_HEIGHT} auto ${MOBILE_HEADER_HEIGHT}`,
          TABLET: `calc(${MOBILE_HEADER_HEIGHT} + ${LOGO_MENU_GAP}) auto ${MOBILE_HEADER_HEIGHT}`,
        }}
      >
        <MediaQueryWrapper
          height="max-content"
          mobile={
            <MobileHeader>
              <Link
                ml={{ MOBILE: SPACE.PX_4, TABLET: SPACE.PX_20 }}
                href={ROUTE.FRONT_PAGE()}
                aria-label="home"
              >
                <Logo width="140px" height="70px" />
              </Link>
              <MenuIconWrapper
                data-test-id={DATA_TEST_ID.HAMBURGER_BTN}
                onClick={() => {
                  if (isMenuOpen) {
                    enableBodyScroll(menuRef.current)
                  } else {
                    disableBodyScroll(menuRef.current)
                  }
                  setIsMenuOpen((prevState) => !prevState)
                }}
              >
                {isMenuOpen ? <Icon.Cross /> : <Icon.Hamburger />}
              </MenuIconWrapper>
            </MobileHeader>
          }
          tablet={
            <Flex
              textAlign="right"
              mx={SPACE.PX_20}
              mt={SPACE.PX_20}
              mb={LOGO_MENU_GAP}
            >
              <Link href={ROUTE.FRONT_PAGE()} aria-label="home">
                <Logo />
              </Link>
            </Flex>
          }
        />
        <Wrapper
          zIndex={Z_INDEX.DASHBOARD_MENU}
          isVisible={isSidebarVisible}
          flexDirection="column"
          overflow="auto"
        >
          {navigation?.(menuRef)}
        </Wrapper>
        <LogoutWrapper
          height="100%"
          zIndex={Z_INDEX.DASHBOARD_MENU}
          isVisible={isSidebarVisible}
          position="relative"
          ref={logoutRef}
          p={SPACE.PX_20}
          onClick={() => {
            setIsLogoutVisible(!isLogoutVisible)
          }}
          flexShrink={0}
          alignItems="center"
        >
          {isLogoutVisible && (
            <LogoutPopup
              flexDirection="column"
              width={POPUP_WIDTH}
              bottom={SPACE.PX_60}
              left={SPACE.PX_20}
              position="fixed"
              zIndex={Z_INDEX.LOGOUT_POPUP}
              isMobile={isMobile}
              onClick={(event) => {
                event.stopPropagation()
              }}
              {...(isMobile ? { maxWidth: rem(250) } : {})}
            >
              {popupActionButtons()}
            </LogoutPopup>
          )}
          <Avatar
            width={AVATAR_SIZE}
            height={AVATAR_SIZE}
            flexShrink={0}
            imageUrl={footerImageUrl}
          />
          <Flex
            ml={SPACE.PX_5}
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Text
              data-test-id={DATA_TEST_ID.SIDE_NAV_NAME_OR_EMAIL}
              color={COLOR.GRAYSCALE_2}
              fontSize={FONT_SIZE.PX_14}
            >
              {footerText}
            </Text>
            {!isMobile && (
              <Icon.ArrowDown
                size={ICON_SIZE.PX_20}
                color={COLOR.GRAYSCALE_3}
              />
            )}
          </Flex>
        </LogoutWrapper>
      </Sidebar>
      <ContentWrapper
        isFullViewport={LIST_OF_PAGES_WITH_FULL_VIEWPORT.includes(
          router?.pathname
        )}
        isMobile={isMobile}
        overflowY="auto"
      >
        {children}
      </ContentWrapper>
    </StyledGrid>
  )
}

SideNavPage.BackButton = BackButton
SideNavPage.Content = Content
SideNavPage.Header = Header
SideNavPage.NavigationWrapper = NavigationWrapper
SideNavPage.NavLink = NavLink
SideNavPage.NavLinkWrapper = NavLinkWrapper
SideNavPage.Title = Title
